import { first, isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { TagComponent } from '@shared/scenes/workflows/config';
import { useUpdateWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { getLatestWorkflowTemplate, isTagComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

interface UpdateTag {
  loading: boolean;
  error?: string;
  updateTag: (
    id: string,
    newTagValues: Partial<API.TagComponentOption>,
  ) => Promise<API.TagComponentOption | undefined>;
}

export function useUpdateTag(workflowTemplateId: string, componentId: string): UpdateTag {
  const [error, setError] = useState<string>();
  const [updateWorkflowTemplate, { loading: updateLoading }] = useUpdateWorkflowTemplate();

  const updateTag = useCallback(
    async (id, newTagValues) => {
      const { workflowTemplate } = await getLatestWorkflowTemplate(workflowTemplateId);

      if (isNil(workflowTemplate)) {
        setError('No workflow template found.');
        return;
      }
      const tagComponent = workflowTemplate.components.find(
        component => component.id === componentId,
      );
      if (isNil(tagComponent) || !isTagComponent(tagComponent)) {
        setError('No component found.');
        return;
      }
      const existingTag = tagComponent.options?.find(option => option.value === id);
      if (isNil(existingTag)) {
        setError('Tag not found.');
        return;
      }
      const newTag: API.TagComponentOption = {
        ...existingTag,
        ...newTagValues,
      };
      const newTagComponent: TagComponent = {
        ...tagComponent,
        options: tagComponent.options?.map(option => (option.value === id ? newTag : option)) ?? [],
      };
      const newWorkflowComponents =
        workflowTemplate?.components?.map(component => {
          return component.id !== componentId ? component : newTagComponent;
        }) ?? [];

      const updateResult = await updateWorkflowTemplate({
        ...workflowTemplate,
        components: newWorkflowComponents,
      });

      if (!isNil(updateResult.errors)) {
        setError(first(updateResult.errors)?.message ?? '');
        return;
      }

      return newTag;
    },
    [workflowTemplateId, componentId, updateWorkflowTemplate],
  );

  return {
    updateTag,
    loading: updateLoading,
    error,
  };
}
